"use client";

import { useEffect, useState } from "react";
import { Slider } from "@/components/slider";
import type { RangeBounds, RangeValue } from "../../hooks/useLifestyleFilters";

interface RangeSliderFieldProps {
  label: string;
  bounds: RangeBounds;
  value: RangeValue | undefined;
  format: (value: number) => string;
  onChange: (value: RangeValue | null) => void;
}

/** Parses a typed bound back out of its formatted text, e.g. "£16,000" or "3.5s". */
function parseBound(text: string): number | null {
  const digits = text.replace(/[^\d.]/g, "");
  const parsed = parseFloat(digits);
  return Number.isFinite(parsed) ? parsed : null;
}

/** One end of the range, editable as text and formatted again on commit. */
function BoundInput({
  label,
  value,
  format,
  onCommit,
}: {
  label: string;
  value: number;
  format: (value: number) => string;
  onCommit: (next: number) => void;
}) {
  const [draft, setDraft] = useState(() => format(value));

  // Dragging the slider is the other way to change this, so the text follows
  // the committed value whenever the user is not mid-edit.
  useEffect(() => setDraft(format(value)), [value, format]);

  // Snap the text back to the committed value. Unparseable input reverts, and
  // a value the parent clamps is corrected by the effect above once it lands.
  const commit = () => {
    const parsed = parseBound(draft);
    setDraft(format(value));
    if (parsed !== null) onCommit(parsed);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={draft}
      onChange={event => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={event => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        commit();
      }}
      className="flex-1 min-w-0 h-12 pl-5 pr-4 text-sm font-roboto text-gray-1 bg-transparent outline-none !border !border-solid !border-black/[0.08] rounded focus:!border-primary transition-colors"
    />
  );
}

/**
 * Two-thumb range control, replacing the Angular rzslider.
 *
 * Uses the project's Radix-backed `Slider`, which renders one thumb per value —
 * passing `[min, max]` gives a range. Radix keeps both thumbs keyboard-operable
 * and handles the "which thumb is nearest" logic on drag. The paired text
 * inputs above it are the design's way in for wide ranges, where dragging to an
 * exact figure is hopeless.
 */
export default function RangeSliderField({ label, bounds, value, format, onChange }: RangeSliderFieldProps) {
  if (!bounds.applicable) return null;

  const current = value ?? { min: bounds.min, max: bounds.max };

  const commit = (min: number, max: number) => {
    const clampedMin = Math.min(Math.max(min, bounds.min), bounds.max);
    const clampedMax = Math.min(Math.max(max, bounds.min), bounds.max);
    const [low, high] = clampedMin <= clampedMax ? [clampedMin, clampedMax] : [clampedMax, clampedMin];

    // Back at both bounds means "no filter", so drop it from state and the URL.
    const isFullRange = low <= bounds.min && high >= bounds.max;
    onChange(isFullRange ? null : { min: low, max: high });
  };

  return (
    <div className="flex flex-col w-full gap-3">
      <div className="flex items-center w-full gap-2">
        <BoundInput
          label={`${label} minimum`}
          value={current.min}
          format={format}
          onCommit={next => commit(next, current.max)}
        />
        <span className="shrink-0 text-sm font-roboto text-gray-1">to</span>
        <BoundInput
          label={`${label} maximum`}
          value={current.max}
          format={format}
          onCommit={next => commit(current.min, next)}
        />
      </div>

      <Slider
        min={bounds.min}
        max={bounds.max}
        step={bounds.step}
        value={[current.min, current.max]}
        onValueChange={([min, max]: number[]) => commit(min, max)}
        formatValue={format}
        variant="filters"
        aria-label={label}
      />
    </div>
  );
}
